import { addDays, addHours, addMinutes, isBefore } from 'date-fns'
import httpClient from '@/utils/http-client'

type CacheItem<T> = {
  data: T;
  date: Date;
};

type CacheSet<T> = { [key: string]: CacheItem<T> };

type ApiConfig = {
  method: string,
  skipError?: boolean,
  url: string,
  data?: Record<string, unknown>,
  params?: Record<string, unknown>
}

let cacheSet: CacheSet<any> = {};
const baseUrl = process.env.VUE_APP_API_BASE_URL || 'https://staging-eagle-api.beusable.net'

function isApiUrl (url: string) {
  if (url.charAt(0) === '/') {
    return true
  }
  return url.indexOf(baseUrl) === 0
}

function getObjectKey (obj: Record<string, unknown>) {
  const key = Object.entries(obj).map(([key, value]) => {
    return `${key}=${value}`
  }).join('&')
  return key
}

function getCacheKey (config: ApiConfig) {
  const method = config.method || 'get'
  const cacheKeys = [method, config.url]

  if (config.params) {
    cacheKeys.push(getObjectKey(config.params))
  }
  if (config.data) {
    cacheKeys.push(getObjectKey(config.data))
  }
  const key = cacheKeys.join('|')
  return key
}

function getTtlDate (ttl: string) {
  const ttlRegex = /^\d+[mMhHdD]$/

  if (!ttlRegex.test(ttl)) {
    console.error(`invalid ttl: ${ttl}`)
    return null
  }
  const now = new Date()
  const value = parseInt(ttl, 10)
  const unit = ttl.charAt(ttl.length - 1).toLowerCase()

  switch (unit) {
    case 'm':
      return addMinutes(now, -value)
    case 'h':
      return addHours(now, -value)
    case 'd':
      return addDays(now, -value)
    default:
      return null
  }
}

function setCache<T>(key: string, data: T): void {
  cacheSet[key] = {
    data,
    date: new Date()
  }
}

function getCache (key: string, ttl: number) {
  const cache = cacheSet[key]
  const ttlDate = getTtlDate(ttl.toString())

  if (cache && ttlDate) {
    if (isBefore(cache.date, ttlDate)) {
      delete cacheSet[key]
    } else {
      return cache.data
    }
  }
  return null
}

export function clearAllApiCache () {
  cacheSet = {}
}

export function request(config: ApiConfig, force = false, ttl = '3m') {
  const isApiRequest = isApiUrl(config.url);
  if (!isApiRequest) {
    return httpClient(config);
  }

  if (force) {
    config.params = config.params || {};
    config.params.t = new Date().getTime();
  }

  const key = getCacheKey(config);
  const ttlMs = parseTTL(ttl);

  if (!force && ttlMs) {
    const cache = getCache(key, ttlMs);
    if (cache) {
      return Promise.resolve({ ...cache, isFromCache: true });
    }
  }

  return httpClient(config).then((response) => {
    const data = response.data;
    setCache(key, Array.isArray(data) ? [...data] : { ...data });
    if (data && typeof data === 'object' && (config.method || 'get').toLowerCase() !== 'get') {
      data.isFromCache = false;
    }

    return data;
  });
}

// TTL 문자열을 밀리초로 변환
function parseTTL(ttl: string | number): number {
  if (typeof ttl === 'number') {
    return ttl;
  }
  const match = ttl.match(/^(\d+)([smhd])$/);
  if (!match) {
    console.warn('Invalid TTL format. Defaulting to 0.');
    return 0;
  }
  const [, value, unit] = match;
  const multipliers: { [key: string]: number } = {
    s: 1000,
    m: 1000 * 60,
    h: 1000 * 60 * 60,
    d: 1000 * 60 * 60 * 24,
  };
  return parseInt(value, 10) * (multipliers[unit] || 0);
}
